/**
 * iOS Fullscreen Helper
 * Detects when running in standalone mode and provides utility functions
 */

export const isStandalone = (): boolean => {
  // Check if running in iOS standalone mode (added to home screen)
  return (
    ('standalone' in window.navigator &&
      (window.navigator as any).standalone === true) ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  );
};

export const shouldPromptInstall = (): boolean => {
  // Don't prompt if already in standalone mode
  if (isStandalone()) {
    console.log('[PWA] Already in standalone mode, skipping prompt');
    return false;
  }

  // Check if user is on iOS
  // iOS 13+ iPads report as Macintosh in UA, so check for touch support
  const userAgent = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
  const isMacWithTouch =
    /Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;
  const isIOS = (isIOSDevice || isMacWithTouch) && !(window as any).MSStream;

  console.log('[PWA] iOS detection:', {
    userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    isIOSDevice,
    isMacWithTouch,
    isIOS,
  });

  if (!isIOS) {
    console.log('[PWA] Not on iOS, skipping prompt');
    return false;
  }

  // Check if user has already been prompted (store in localStorage)
  const hasBeenPrompted = localStorage.getItem('ios-install-prompted');
  console.log('[PWA] Has been prompted before:', hasBeenPrompted);

  return !hasBeenPrompted;
};

export const markInstallPrompted = (): void => {
  localStorage.setItem('ios-install-prompted', 'true');
};

// Force fullscreen on supported browsers
export const requestFullscreen = async (
  element: HTMLElement = document.documentElement
): Promise<void> => {
  try {
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else if ((element as any).webkitRequestFullscreen) {
      await (element as any).webkitRequestFullscreen();
    } else if ((element as any).mozRequestFullScreen) {
      await (element as any).mozRequestFullScreen();
    } else if ((element as any).msRequestFullscreen) {
      await (element as any).msRequestFullscreen();
    }
  } catch (error) {
    console.warn('Fullscreen request failed:', error);
  }
};

export const exitFullscreen = async (): Promise<void> => {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      await (document as any).webkitExitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      await (document as any).mozCancelFullScreen();
    } else if ((document as any).msExitFullscreen) {
      await (document as any).msExitFullscreen();
    }
  } catch (error) {
    console.warn('Exit fullscreen failed:', error);
  }
};

export const isFullscreen = (): boolean => {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement
  );
};
